import React, { useState } from 'react';
import { Button } from '../ui/button';
import { validateQuizJSON } from '../../lib/utils';
import type { Quiz } from '../../types';

interface JSONImporterProps {
  onImport: (quiz: Quiz) => void;
}

const JSONImporter: React.FC<JSONImporterProps> = ({ onImport }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsedQuiz = JSON.parse(content);
        
        if (!validateQuizJSON(parsedQuiz)) {
          setError('Invalid quiz JSON format. Please check your file structure.');
          setIsLoading(false);
          return;
        }

        // Convert to proper Quiz object
        const quiz: Quiz = {
          title: parsedQuiz.title,
          description: parsedQuiz.description || 'Imported quiz',
          questions: parsedQuiz.questions.map((q: any) => ({
            text: q.text,
            timeLimit: q.timeLimit || 30, // Default 30 seconds
            pointValue: q.pointValue || 100, // Default 100 points
            options: q.options
          }))
        };

        onImport(quiz);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to parse JSON file. Please check the file format.');
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setIsLoading(false);
    };

    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const template: Quiz = {
      title: "Example Quiz",
      description: "A sample quiz template",
      questions: [
        {
          text: "What is 2+2?",
          timeLimit: 30,
          pointValue: 100,
          options: [
            { text: "3", isCorrect: false },
            { text: "4", isCorrect: true },
            { text: "5", isCorrect: false },
            { text: "6", isCorrect: false }
          ]
        }
      ]
    };

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(template, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "quiz_template.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  return (
    <div className="border-2 border-border rounded-2xl p-6 mb-6 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
      <h3 className="text-xl font-heading font-bold mb-4">Import Quiz from JSON</h3>
      
      <div className="flex flex-col gap-4 mb-4">
        <div>
          <label 
            htmlFor="json-upload" 
            className="block text-sm font-semibold mb-2"
          >
            Upload JSON File
          </label>
          <input
            id="json-upload"
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            disabled={isLoading}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-3 file:px-6
              file:rounded-lg file:border-0
              file:text-sm file:font-semibold
              file:bg-primary file:text-primary-foreground
              hover:file:bg-primary/90 file:transition-colors
              cursor-pointer"
          />
        </div>

        {error && (
          <div className="text-destructive text-sm font-medium bg-destructive/10 p-3 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleDownloadTemplate}
          size="sm"
        >
          Download Template
        </Button>
      </div>
    </div>
  );
};

export default JSONImporter;